import React from 'react';
import { useForm } from 'react-hook-form';
import i18n from 'i18next';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { DropDownListController } from '@components-clean/core/drop-down-list';
import { FilterListDataItem } from '@components-clean/core/filter-list/filter-list-item';
import { TextBoxController } from '@components-clean/core/text-box';
import { useTranslation } from '@hooks/index';

interface AddEditFilterListItemProps<T> {
    selectData: T[];
    onSubmit: (data: { fieldKey: T | undefined; labelText: FilterListDataItem['labelText'] }) => void;
    onClose: () => void;
    reservedItems: FilterListDataItem[];
    currentItem?: FilterListDataItem | null;
    valueField?: keyof T & string;
    displayField?: keyof T & string;
}

const getAvailableSelectItems = <T,>(
    selectData: T[],
    reservedItems: FilterListDataItem[],
    currentItem?: FilterListDataItem | null,
    field?: keyof T & string,
) => {
    const usedFields = reservedItems.map((item) => {
        if (field && field in item) {
            return item[field as keyof FilterListDataItem];
        }

        return item.fieldKey;
    });
    return selectData.filter((item) => {
        let isFieldUsed = false;
        if (typeof item === 'string') {
            isFieldUsed = usedFields.indexOf(item) === -1;
        }
        if (typeof item === 'object' && field) {
            isFieldUsed = usedFields.findIndex((usedField) => usedField === item?.[field]) === -1;
        }
        return (
            (currentItem &&
                (typeof item === 'string'
                    ? item === currentItem.fieldKey
                    : field && item[field] === currentItem.fieldKey)) ||
            isFieldUsed
        );
    });
};

const validateLabelUniqueness = (
    value: string,
    reservedItems: FilterListDataItem[],
    currentItem?: FilterListDataItem | null,
) => {
    const reservedLabels = reservedItems.map((item) => item.labelText);

    // The second part allows us to input the same value if we edit an item
    if (reservedLabels.indexOf(value) !== -1 && !(currentItem?.labelText === value)) {
        return i18n.t('labelAlreadyInUse');
    }
};

const getFieldKeyValue = <T,>(currentItem: FilterListDataItem, selectData: T[], field?: keyof T & string) => {
    if (typeof selectData[0] === 'string') {
        return currentItem.fieldKey;
    }
    return selectData.find((item) => field && item[field] === currentItem.fieldKey);
};

const AddEditFilterListItem = <T,>({
    selectData,
    currentItem,
    reservedItems,
    onSubmit,
    onClose,
    valueField,
    displayField,
}: AddEditFilterListItemProps<T>) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty },
    } = useForm<{ fieldKey: T | undefined; labelText: FilterListDataItem['labelText'] }>({
        mode: 'all',
        defaultValues: {
            fieldKey: ((currentItem && getFieldKeyValue(currentItem, selectData, valueField)) ?? null) as T,
            labelText: currentItem?.labelText ?? '',
        },
    });

    const selectItems = getAvailableSelectItems(selectData, reservedItems, currentItem, valueField);
    const dialogTitle = t(currentItem ? 'admin:editFilterOption' : 'admin:addFilterOption');
    const primaryBtnLabel = t(currentItem ? 'update' : 'save');

    return (
        <DraggableDialog
            title={dialogTitle}
            dataTestId="add-edit-dialog"
            onClose={onClose}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty}
            initialWidth={700}
        >
            <form id="add-edit-list-view-item-form" className="k-w-full">
                <DropDownListController
                    control={control}
                    name="fieldKey"
                    data={selectItems}
                    data-testid="field-select"
                    className="!k-mb-5"
                    label="Field *"
                    textField={displayField}
                    dataItemKey={valueField}
                    disabled={currentItem?.isDefault}
                    required
                />
                <TextBoxController
                    control={control}
                    name="labelText"
                    data-testid="label-input"
                    label="Label *"
                    maxLength={30}
                    validateRules={{
                        uniqueLabel: (value) =>
                            validateLabelUniqueness((value as string) ?? '', reservedItems, currentItem),
                    }}
                    required
                />
            </form>
        </DraggableDialog>
    );
};

export default AddEditFilterListItem;
